import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Registry, User, UserViewType } from "../../types/user";
import { AppDispatch, RootState } from "../store";
import { fetchItems, putItem } from "../queries";
import { defaultViews } from "../config/tables";

const DEBOUNCE_DURATION = 2000;

interface RegistryState {
  data: Registry | null;
  loading: boolean;
  ready: boolean;
  error: boolean;
  pending: number;

  // Local instance of registry that contains unsaved changes (used for debouncing updates)
  registry: Registry | null;
}

const initialState: RegistryState = {
  data: null,
  loading: false,
  ready: false,
  error: false,
  pending: 0,

  registry: null
};


// Async thunk for fetching registry
export const initializeRegistry = createAsyncThunk<
  Registry,
  void,
  { state: RootState }
>('registry/initialize', async (_, { getState, rejectWithValue }) => {
  try {
    const { email, id } = getState().user.userData;
    const data: User[] = await fetchItems(`db/user`, { email }, { credentials: 'same-origin' });
    let registry: Registry = (Array.isArray(data) && data.length > 0 ? data[0]?.registry ?? {} : {}) as Registry;

    const missingViewTypes = Object.values(UserViewType).filter(
      (type) => !registry?.views?.[type],
    );

    if (!registry?.views || !registry?.registryValue || missingViewTypes.length > 0) {
      registry = {
        views: {
          [UserViewType.Customer]:
            registry?.views?.[UserViewType.Customer] ?? [defaultViews[UserViewType.Customer]],
          [UserViewType.Opportunity]:
            registry?.views?.[UserViewType.Opportunity] ?? [defaultViews[UserViewType.Opportunity]],
          [UserViewType.CustomerTree]:
            registry?.views?.[UserViewType.CustomerTree] ?? [defaultViews[UserViewType.CustomerTree]],
        },
        registryValue: registry?.registryValue ?? {},
        history: registry?.history ?? [],
        notifications: registry?.notifications ?? {},
      };

      await putItem(`db/user`, { id, email, registry });
    }

    return registry;
  } catch (err) {
    return rejectWithValue(err);
  }
});

let debounceTimeout: ReturnType<typeof setTimeout> | null = null;
const debouncedUpdateRegistry = createAsyncThunk<
  Registry,
  { registry: Registry; email: string; id: number },
  { state: RootState }
>('registry/update', async (payload, { dispatch, rejectWithValue }) => {

  // Debounce logic
  return new Promise<Registry>((resolve, reject) => {
    if (debounceTimeout) clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(async () => {
      try {
        const result = await putItem(`db/user`, {
          id: payload.id,
          email: payload.email,
          registry: payload.registry,
        });
        resolve(payload.registry);
      } catch (err) {
        reject(rejectWithValue(err));
      }
    }, DEBOUNCE_DURATION);
  });
});

export const updateRegistry = (payload: Registry) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(registrySlice.actions.updateLocalRegistry(payload));
    const { registry } = getState().registry;
    const { email, id } = getState().user.userData;

    if (registry) {
      dispatch(debouncedUpdateRegistry({
        registry: registry,
        email: email,
        id: id,
      }));
    }
  };

const registrySlice = createSlice({
  name: 'registry',
  initialState,
  reducers: {
    setRegistryReady(state, action) {
      state.ready = action.payload;
    },
    setRegistryError(state, action) {
      state.error = action.payload;
    },
    updateLocalRegistry(state, action) {
      state.registry = {
        ...state.registry,
        ...action.payload,
      };
    },
    resetLocalRegistry(state) {
      state.registry = state.data;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(initializeRegistry.pending, (state) => {
        state.loading = true;
        state.ready = false;
        state.error = false;
      })
      .addCase(initializeRegistry.fulfilled, (state, action) => {
        state.data = action.payload;
        state.registry = action.payload;
        state.loading = false;
        state.ready = true;
        state.error = false;
      })
      .addCase(initializeRegistry.rejected, (state) => {
        state.loading = false;
        state.ready = false;
        state.error = true;
      })
      // Update
      .addCase(debouncedUpdateRegistry.pending, (state) => {
        state.loading = true;
        state.ready = false;
        state.error = false;
        state.pending += 1;
      })
      .addCase(debouncedUpdateRegistry.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
        state.ready = true;
        state.error = false;
        state.pending = Math.max(0, state.pending - 1);
        if (state.pending === 0) {
          state.registry = action.payload;
        }
      })
      .addCase(debouncedUpdateRegistry.rejected, (state) => {
        state.loading = false;
        state.ready = false;
        state.error = true;
        state.pending = Math.max(0, state.pending - 1);
      });
  },
});

export default registrySlice.reducer;